import React, {
  createContext,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { isBrowser } from '@agria/utils';

declare global {
  interface Window {
    experiments?: Record<string, any>;
  }
}

export interface ExperimentsContextProps {
  experiments: Record<string, any>;
  setExperiments: React.Dispatch<React.SetStateAction<object>>;
}

export const ExperimentsContext = createContext({} as ExperimentsContextProps);
ExperimentsContext.displayName = 'ExperimentsContext';

export const ExperimentsContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [experiments, setExperiments] = useState<object>({
    AGRIA115: process.env.GATSBY_EXPERIMENT_AGRIA115,
    AGRIA119: process.env.GATSBY_EXPERIMENT_AGRIA119,
    AGRIA121: process.env.GATSBY_EXPERIMENT_AGRIA121,
    AGRIA122: process.env.GATSBY_EXPERIMENT_AGRIA122,
    AGRIA123: process.env.GATSBY_EXPERIMENT_AGRIA123,
    AGRIA124: process.env.GATSBY_EXPERIMENT_AGRIA124,
    AGRIA126: process.env.GATSBY_EXPERIMENT_AGRIA126,
    AGRIA128: process.env.GATSBY_EXPERIMENT_AGRIA128,
    AGRIA132: process.env.GATSBY_EXPERIMENT_AGRIA132,
    AGRIA133: process.env.GATSBY_EXPERIMENT_AGRIA133,
    AGRIA135: process.env.GATSBY_EXPERIMENT_AGRIA135,
    AGRIA136: process.env.GATSBY_EXPERIMENT_AGRIA136,
    AGRIA137: process.env.GATSBY_EXPERIMENT_AGRIA137,
    AGRIA138: process.env.GATSBY_EXPERIMENT_AGRIA138,
    LBIS001: process.env.GATSBY_EXPERIMENT_LBIS001,
    LBIS002: process.env.GATSBY_EXPERIMENT_LBIS002,
  });

  useEffect(() => {
    if (!isBrowser) return;

    let observer: MutationObserver | null = null;
    let intervalId: NodeJS.Timeout | null = null;

    const updateExperiments = (source = 'unknown') => {
      if (window.experiments) {
        setExperiments((current) => ({
          ...current,
          ...window.experiments,
        }));
      }
    };

    const setupExperimentsWatcher = () => {
      // Initial check
      updateExperiments('initial-check');

      // Watch for DOM changes that might indicate VWO has loaded
      observer = new MutationObserver((mutations) => {
        let shouldCheck = false;

        mutations.forEach((mutation) => {
          // Only check if any script tags were added (VWO might inject scripts)
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as Element;
                if (
                  element.tagName === 'SCRIPT' ||
                  element.querySelector('script')
                ) {
                  shouldCheck = true;
                }
              }
            });
          }
        });

        // Only check for experiments if we detected a script addition
        if (shouldCheck) {
          // Give VWO a moment to set window.experiments after script execution
          setTimeout(() => updateExperiments('script-injection'), 50);
        }
      });

      // Observe the entire document for changes
      observer.observe(document, {
        childList: true,
        subtree: true,
      });

      // Fallback: Also poll periodically for the first few seconds
      // in case VWO sets experiments without DOM changes
      let pollCount = 0;
      const maxPolls = 20; // 2 seconds with 100ms intervals

      intervalId = setInterval(() => {
        updateExperiments(`polling-${pollCount + 1}`);
        pollCount += 1;

        if (pollCount >= maxPolls) {
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
        }
      }, 100);
    };

    // Start watching
    setupExperimentsWatcher();

    // Cleanup function
    return () => {
      if (observer) {
        observer.disconnect();
      }
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);

  const contextValues = useMemo(
    () => ({
      experiments,
      setExperiments,
    }),
    [experiments, setExperiments],
  );

  return (
    <ExperimentsContext.Provider value={contextValues}>
      {children}
    </ExperimentsContext.Provider>
  );
};
